import Markdown from '@/components/atoms/Markdown/Markdown';
import VideoPlayer from '@/modules/content/common/VideoPlayer';
import { TContentType } from '@/types/Content.types';
import { Flex, Image, Text, VStack } from '@chakra-ui/react';
import React, { useEffect, useMemo } from 'react';
import BrandedThumbnailPreview from '../components/BrandedThumbnailPreview';
import { TBrandedFormValues } from '../types';

type Props = {
  type: TContentType;
  values: TBrandedFormValues;
  thumbnailFile: File | null;
  defaultThumbnailUrl?: string | null;
};

// Extracts an 11-char YouTube video ID from the common URL formats (or a raw ID).
const parseYouTubeId = (url: string): string | null => {
  const trimmed = url.trim();
  if (/^[a-zA-Z0-9_-]{11}$/.test(trimmed)) return trimmed;
  const match = trimmed.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
  );
  return match ? match[1] : null;
};

const ContentPreviewStep = ({ type, values, thumbnailFile, defaultThumbnailUrl }: Props) => {
  // Object URLs for any newly-picked section/subsection images (the thumbnail card
  // derives its own URL internally).
  const objectUrls = useMemo(() => {
    const map = new Map<File, string>();
    const add = (file?: File | null) => {
      if (file && !map.has(file)) map.set(file, URL.createObjectURL(file));
    };
    add(thumbnailFile);
    for (const section of values.sections) {
      add(section.imageFile);
      section.subsections.forEach((sub) => add(sub.imageFile));
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thumbnailFile, values.sections]);

  useEffect(() => () => objectUrls.forEach((url) => URL.revokeObjectURL(url)), [objectUrls]);

  const srcFor = (file?: File | null, url?: string | null) =>
    (file ? objectUrls.get(file) : undefined) || url || undefined;

  const thumbnailSrc = srcFor(thumbnailFile, defaultThumbnailUrl);

  return (
    <VStack align="center" width="100%" gap="28px" maxWidth="1040px">
      <Text fontWeight={800} fontSize="20px" color="text.darkBlue">
        Content Preview
      </Text>

      {/* Two equal columns: the branded card (read-only) next to the rendered content. */}
      <Flex width="100%" gap="40px" align="flex-start" justify="center" flexWrap="wrap">
        <Flex flex={1} minWidth="380px" justify="center">
          <BrandedThumbnailPreview
            type={type}
            title={values.title}
            description={values.description}
            domains={values.domains}
            file={thumbnailFile}
            existingUrl={defaultThumbnailUrl}
            onFileChange={() => {}}
            readOnly
          />
        </Flex>

        <Flex flex={1} minWidth="320px" justify="center">
          <VStack align="stretch" width="100%" maxWidth="480px" gap="16px">
            <Text fontWeight={800} fontSize="22px" color="text.darkBlue">
              {values.title || 'Untitled'}
            </Text>

            {type === 'post' && <Markdown>{values.description || '_No content yet._'}</Markdown>}

            {type === 'video' && (
              <VStack align="stretch" gap="12px">
                <VideoPlayer
                  source={values.videoSource}
                  youtubeVideoId={parseYouTubeId(values.youtubeUrl)}
                  muxStatus={values.muxStatus}
                  muxPlaybackId={values.muxPlaybackId}
                  posterUrl={thumbnailSrc}
                  title={values.title}
                  pendingUpload={Boolean(values.videoFile)}
                  emptyHint="Add a valid YouTube link to preview the video."
                />
                {values.fullDescription && (
                  <Text color="text.darkBlue" fontSize="15px" whiteSpace="pre-wrap">
                    {values.fullDescription}
                  </Text>
                )}
              </VStack>
            )}

            {type === 'article' &&
              values.sections.map((section, index) => {
                const sectionImg = srcFor(section.imageFile, section.imageUrl);
                return (
                  <VStack key={section._id} align="stretch" gap="8px">
                    {section.title && (
                      <Text fontWeight={700} fontSize="18px" color="text.darkBlue">
                        {section.title}
                      </Text>
                    )}
                    <Markdown>{section.text || '_No content yet._'}</Markdown>
                    {sectionImg && (
                      <Image src={sectionImg} alt={section.title || `Section ${index + 1}`} borderRadius="8px" />
                    )}

                    {section.subsections.map((sub) => {
                      const subImg = srcFor(sub.imageFile, sub.imageUrl);
                      return (
                        <VStack key={sub._id} align="stretch" gap="6px" paddingLeft="12px">
                          {sub.title && (
                            <Text fontWeight={700} fontSize="15px" color="text.mediumBlue">
                              {sub.title}
                            </Text>
                          )}
                          <Markdown>{sub.text || '_No content yet._'}</Markdown>
                          {subImg && <Image src={subImg} alt={sub.title || 'Subsection'} borderRadius="8px" />}
                        </VStack>
                      );
                    })}
                  </VStack>
                );
              })}

            {type === 'article' && values.sections.length === 0 && (
              <Text color="text.mediumGray" fontSize="14px">
                No sections yet.
              </Text>
            )}
          </VStack>
        </Flex>
      </Flex>
    </VStack>
  );
};

export default ContentPreviewStep;
